import _ from "lodash";
import moment from "moment";
import uuidv4 from "uuid";
import {Modal} from "antd";
import {getFilteredTree, getMappedTree, getMappedTreeDeptFirst, getNode} from "./tree";
import {getAutoAnnotations, getFilteredAnnotations} from "./artefacts";
import {getArtefactTripletFromUrn, getStringFromArtefactTriplet} from "./sdmxJson";
import {getLocalizedStr} from "../middlewares/i18n/utils";
import {
  AUTO_ANNOTATION_CUSTOM_IS_PRESENTATIONAL_KEY,
  AUTO_ANNOTATION_FILE_PATH_KEY,
  AUTO_ANNOTATION_IS_MULTILINGUAL_KEY,
  AUTO_ANNOTATION_RESTRICTED_FOR_PUBLICATION_KEY
} from "./annotations";
import {getEmptyDictionaryForLanguages} from "./languages";

export const REPORT_ID_ANNOTATION_ID = "ReportId";
export const METADATA_SET_ID_ANNOTATION_ID = "MetadataSetId";

export const REPORT_STATE_ID_ANNOTATION_ID = "ReportStateId";
export const REPORT_STATE_NAME_ANNOTATION_ID = "ReportStateName";

export const REPORT_STATE_NOT_PUBLISHED = "NOT_PUBLISHED";
export const REPORT_STATE_PUBLISHED = "PUBLISHED";
export const REPORT_STATE_DRAFT = "DRAFT";

export const REPORT_STATE_DRAFT_ID = "4";
export const REPORT_STATE_NOT_PUBLISHED_ID = "2";

export const CATALOG_TARGET_ID = "CATALOG_TARGET_ID";
export const DATAFLOW_TARGET_ID = "DATAFLOW_TARGET_ID";
export const CATALOG_IDENTIFIER = "CATALOG_IDENTIFIER";
export const DATASET_IDENTIFIER = "DATASET_IDENTIFIER";

const FOLDER_KEY = "FOLDER___";
export const EMPTY_REPORT_ATTRIBUTE_KEY = "EMPTY_REPORT_ATTRIBUTE___";

export const ATTRIBUTE_TEXT_TYPE_BOOLEAN = "boolean";

export const ATTRIBUTE_TEXT_TYPE_STRING = "string";

export const ATTRIBUTE_TEXT_TYPE_XHTML = "xhtml";

export const ATTRIBUTE_TEXT_TYPE_DATETIME = "datetime";

export const ATTRIBUTE_TEXT_TYPE_GREGORIANDAY = "gregorianday";

export const ATTRIBUTE_TEXT_TYPE_TIME = "time";

export const ATTRIBUTE_TEXT_TYPE_ALPHANUMERIC = "alphanumeric";

export const ATTRIBUTE_TEXT_TYPE_ALPHA = "alpha";

export const ATTRIBUTE_TEXT_TYPES_INTEGER = [
  "numeric",
  "integer",
  "biginteger",
  "long",
  "short",
  "count"
];

export const ATTRIBUTE_TEXT_TYPES_DECIMAL = [
  "decimal",
  "float",
  "double",
  "inclusivevaluerange",
  "exclusivevaluerange",
  "incremental"
];

export const ATTRIBUTE_TEXT_TYPE_GREGORIANYEAR = "gregorianyear";
export const ATTRIBUTE_TEXT_TYPE_GREGORIANYEARMONTH = "gregorianyearmonth";
export const ATTRIBUTE_TEXT_TYPE_DAY = "day";
export const ATTRIBUTE_TEXT_TYPE_MONTH = "month";
export const ATTRIBUTE_TEXT_TYPE_YEAR = "year"; // SDMX v2.0
export const ATTRIBUTE_TEXT_TYPE_MONTHDAY = "monthday";
export const ATTRIBUTE_TEXT_TYPE_YEARMONTH = "yearmonth"; // SDMX v2.0
export const ATTRIBUTE_TEXT_TYPE_DATE = "date"; // SDMX v2.0
export const ATTRIBUTE_TEXT_TYPE_URI = "uri";

export const ATTRIBUTE_TEXT_TYPE_FORMAT_DATETIME = "YYYY-MM-DD HH:mm:ss";
export const ATTRIBUTE_TEXT_TYPE_FORMAT_DATE = "YYYY-MM-DD";
export const ATTRIBUTE_TEXT_TYPE_FORMAT_TIME = "HH:mm:ss";

const AUTO_ANNOTATIONS = [
  "repattributeid"
];

/** structures utilities **/

// visit report structure and call map for all node
function getMappedReportTree(tree, map) {
  tree = _.cloneDeep(tree);
  tree = tree.map(root => map(root));
  tree = tree.map(subTree => {
    if (subTree && subTree.attributeSet && subTree.attributeSet.reportedAttributes) {
      subTree.attributeSet.reportedAttributes = getMappedReportTree(subTree.attributeSet.reportedAttributes, map);
    }
    return subTree;
  });
  return tree;
}

// visit report structure and return, if present, the matching node
export function getReportNode(tree, test) {

  if (!(tree !== null && tree.length)) return null;

  const foundNodes = tree.filter(test);

  if (foundNodes.length) {
    return foundNodes[0];
  } else {
    const foundNodesInChild =
      tree
        .filter(node => node.attributeSet.reportedAttributes)
        .map(node => getReportNode(node.attributeSet.reportedAttributes, test))
        .filter(result => result !== null);

    if (foundNodesInChild.length) {
      return foundNodesInChild[0];
    } else {
      return null;
    }
  }
}

// check if an attribute has a valorized "texts" field or not
export const isAttributeEmpty = attribute => {
  if (!attribute) {
    return true;
  }

  const isValidText = text => {
    if (!text) {
      return false;
    }

    let parsedText = (new DOMParser().parseFromString(text, "text/html").documentElement.textContent || "").trim();

    return parsedText.length > 0;
  }

  if (!attribute.isFolder && !attribute.isPresentational) {
    if (getAttributeType(attribute).toLowerCase() === ATTRIBUTE_TEXT_TYPE_BOOLEAN) {
      return false;
    } else if (
      attribute.texts === null ||
      attribute.texts === undefined ||
      ((typeof attribute.texts === 'string' || attribute.texts instanceof String) && !isValidText(attribute.texts)) ||
      ((typeof attribute.texts === 'object' || attribute.texts instanceof Object) && _(attribute.texts).pickBy(val => isValidText(val)).keys().value().length === 0)
    ) {
      return true;
    }
  }
  return false;
};

const getRegexExp = attributeType => {

  const type = (attributeType || "").toLowerCase();

  if (type === ATTRIBUTE_TEXT_TYPE_ALPHA) {
    return /^[a-z ]*$/gim;
  } else if (type === ATTRIBUTE_TEXT_TYPE_ALPHANUMERIC) {
    return /^[a-z0-9 ]*$/gim;
  } else if (ATTRIBUTE_TEXT_TYPES_INTEGER.includes(type)) {
    return /^[0-9]*$/gim;
  } else if (ATTRIBUTE_TEXT_TYPES_DECIMAL.includes(type)) {
    return /^([+-])?([0-9]+(([.,])[0-9]+)?|([.,])[0-9]+)$/gim;
  } else if (type === ATTRIBUTE_TEXT_TYPE_DAY) {
    return /^(0[1-9]|[12][0-9]|3[01])$/gim;
  } else if (type === ATTRIBUTE_TEXT_TYPE_MONTH) {
    return /^(0[1-9]|1[0-2])$/gim;
  } else if (type === ATTRIBUTE_TEXT_TYPE_YEAR || type === ATTRIBUTE_TEXT_TYPE_GREGORIANYEAR) {
    return /^[0-9]{4}$/gim;
  } else if (type === ATTRIBUTE_TEXT_TYPE_MONTHDAY) {
    return /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/gim;
  } else if (type === ATTRIBUTE_TEXT_TYPE_YEARMONTH || type === ATTRIBUTE_TEXT_TYPE_GREGORIANYEARMONTH) {
    return /^([0-9]{4})-(0[1-9]|1[0-2])$/gim;
  } else if (type === ATTRIBUTE_TEXT_TYPE_DATE) {
    return /^([0-9]{4})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/gim;
  } else if (type === ATTRIBUTE_TEXT_TYPE_URI) {
    return /^(https:\/\/|http:\/\/)?(www\.)?[a-zA-Z0-9@:%._+~#=-]{2,256}\.[a-z]{2,6}([a-zA-Z0-9@:%_+.~\-#?&/=,]*)$/gi
  } else {
    return /^.*$/gim;
  }
};

// check if an attribute has a valid "texts" field or not
export const isAttributeValid = attribute => {

  if (isAttributeEmpty(attribute)) {
    return true;
  }

  const attributeType = getAttributeType(attribute);
  const values = [];

  if (!attribute.isMultiLang) {
    values.push(attribute.texts);
  } else {
    for (let key in attribute.texts) {
      if (attribute.texts.hasOwnProperty(key)) {
        values.push(attribute.texts[key]);
      }
    }
  }

  return !values.find(val => !getRegexExp(attributeType).test(val));
};

// check if an attribute has a valorized AND valid "texts" field or not
export const isAttributeValorizedAndValid = attribute => (!isAttributeEmpty(attribute) && isAttributeValid(attribute));

// gets a tree structure from msd
export const getMsdTree = (msd, conceptSchemes, appLang, dataLangs, annotationsConfig, permissions) => {

  const customIsPresentational = getCustomIsPresentationalFromAnnotations(msd, annotationsConfig);

  // iterates on msd structure and initialize utility fields
  let msdTree = msd.metadataStructureComponents.reportStructures.map(
    reportStructure => ({
      target: reportStructure.metadataTargets[0],
      metadataAttributes: getMappedTree(
        reportStructure.metadataAttributes,
        "metadataAttributes",
        node => {
          const isMultiLangAnnot = node.annotations && node.annotations.find(annot =>
            (annot.type || "").toLowerCase() === (annotationsConfig[AUTO_ANNOTATION_IS_MULTILINGUAL_KEY].type || "").toLowerCase());
          const isMultiLang = isMultiLangAnnot && isMultiLangAnnot.title === "True";

          const attributeType = getAttributeType(node).toLowerCase();

          return {
            ...node,
            texts: null,
            metadataAttributes: (node.metadataAttributes || []).map(child => ({
              ...child,
              parent: node.id
            })),
            annotations: [],
            autoAnnotations: [],
            isMultiLang: node.id === "DCAT_AP_IT_DATASET_IDENTIFIER"
              ? false
              : (attributeType === ATTRIBUTE_TEXT_TYPE_STRING || attributeType === ATTRIBUTE_TEXT_TYPE_XHTML || attributeType === ATTRIBUTE_TEXT_TYPE_URI)
                ? true
                : !!isMultiLang,
            attachment: getEmptyDictionaryForLanguages(dataLangs, null),
            attachmentName: getEmptyDictionaryForLanguages(dataLangs, null),
            attachmentUploaded: getEmptyDictionaryForLanguages(dataLangs, false),
            isPublic: true,
            isPresentational: !customIsPresentational
              ? node.isPresentational
              : (node.isPresentational || (node.metadataAttributes || []).length > 0)
          }
        }
      ),
      identifiableTargets: msd.metadataStructureComponents.metadataTargets
        .find(target => target.id === reportStructure.metadataTargets[0])
        .identifiableObjectTargets
        .map(identifiableTarget => _.pick(identifiableTarget, "id", "objectType"))
    })
  );

  // iterates on msd tree and add folder nodes to handle attribute cardinality
  const getFolderFromNode = node => ({
    ...node,
    id: FOLDER_KEY + node.id,
    isFolder: true,
    metadataAttributes: [{...node, metadataAttributes: []}]
  });
  msdTree = msdTree.map(el => {
    let tempTree = [];
    getMappedTree(
      [el],
      "metadataAttributes",
      node => {
        node.metadataAttributes.forEach(child => {
          const parentNode = getNode(tempTree, "metadataAttributes", ({id}) => id === child.parent);

          if (child.minOccurs !== 1 || child.maxOccurs !== 1) {
            (parentNode ? parentNode.metadataAttributes : tempTree).push(getFolderFromNode(child))
          } else {
            (parentNode ? parentNode.metadataAttributes : tempTree).push({...child, metadataAttributes: []})
          }
        });
        return node
      }
    );

    return ({
      ...el,
      metadataAttributes: tempTree
    })
  });

  // iterates on msd tree and add all the required attributes + an empty one as child of the corresponding folder
  msdTree = msdTree.map(el => ({
    ...el,
    metadataAttributes: getMappedTree(
      el.metadataAttributes,
      "metadataAttributes",
      node => {
        if (node.isFolder) {
          node.id = node.id.substring(FOLDER_KEY.length);

          const metadataAttributes = [];
          for (let i = 1; i <= node.minOccurs; i++) {
            metadataAttributes.push({
              ...node.metadataAttributes[0],
              id: `${i}. ${node.id}`
            })
          }
          metadataAttributes.push({
            ...node.metadataAttributes[0],
            id: (EMPTY_REPORT_ATTRIBUTE_KEY + node.id)
          });
          node.metadataAttributes = metadataAttributes;
        }
        return node
      }
    )
  }));

  // iterates on msd tree and add to all node the corresponding name taken from associated conceptScheme
  msdTree = msdTree.map(el => ({
    ...el,
    metadataAttributes: getMappedTree(
      el.metadataAttributes,
      "metadataAttributes",
      node => {
        const nodeStr = getStringFromArtefactTriplet(getArtefactTripletFromUrn(node.conceptIdentity));
        const nodeConceptScheme = conceptSchemes.find(conceptScheme => getStringFromArtefactTriplet(conceptScheme) === nodeStr);
        if (nodeConceptScheme) {
          const concept = nodeConceptScheme.concepts.find(concept => concept.id === node.conceptIdentity.split(".").pop());
          if (concept) {
            const prefix = node.id.match(/\d+. /g);
            return {
              ...node,
              name: (prefix ? prefix : '') + getLocalizedStr(concept.name, appLang, dataLangs)
            }
          }
        }
        return {
          ...node,
          name: node.id
        }
      })
  }));

  return msdTree
};

// gets report structure from SDMXJson report
export const getReportStructureFromSdmxJsonReport = (msdTree, target, report, dataLanguages, t, annotationsConfig, permissions) => {

  // gets text from attribute
  const getTexts = (node, attribute) => {
    if (attribute.texts === null || attribute.texts === undefined) {
      return null
    }

    const attributeType = getAttributeType(node).toLowerCase();

    let ret;

    if (node.localRepresentation && node.localRepresentation.enumeration) {
      ret = attribute.texts;

    } else if (attributeType === ATTRIBUTE_TEXT_TYPE_DATETIME) {
      ret = _.forOwn(attribute.texts, (val, key) => attribute.texts[key] = moment(val, ATTRIBUTE_TEXT_TYPE_FORMAT_DATETIME));

    } else if (attributeType === ATTRIBUTE_TEXT_TYPE_GREGORIANDAY) {
      ret = _.forOwn(attribute.texts, (val, key) => attribute.texts[key] = moment(val, ATTRIBUTE_TEXT_TYPE_FORMAT_DATE));

    } else if (attributeType === ATTRIBUTE_TEXT_TYPE_TIME) {
      ret = _.forOwn(attribute.texts, (val, key) => attribute.texts[key] = moment(val, ATTRIBUTE_TEXT_TYPE_FORMAT_TIME));

    } else if (attributeType === ATTRIBUTE_TEXT_TYPE_BOOLEAN) {
      ret = _.forOwn(attribute.texts, (val, key) => attribute.texts[key] = (val || "").toLowerCase() === "true");

    } else {
      ret = attribute.texts;
    }

    if (ret.und !== undefined) {
      if (ret.und === true || ret.und === false || moment.isMoment(ret.und)) {
        return ret.und;
      } else {
        let decodedText;
        try {
          decodedText = decodeURIComponent(ret.und);
        } catch (e) {
          decodedText = ret.und;
        }
        return decodedText;
      }

    } else {
      return _.forOwn(ret, (val, key) => {
        if (val === true || val === false || moment.isMoment(val)) {
          return val;
        } else {
          let decodedText;
          try {
            decodedText = decodeURIComponent(val);
          } catch (e) {
            decodedText = val;
          }
          ret[key] = decodedText;
        }
      });
    }
  };

  const tree = _.cloneDeep(msdTree);

  // gets the corresponding report structures and add an unique key
  let reportStructure = [];
  reportStructure = tree.find(el => el.target === target).metadataAttributes;
  reportStructure = getMappedTree(
    reportStructure,
    "metadataAttributes",
    repStrNode => ({
      ...repStrNode,
      nodeKey: uuidv4()
    }));

  // if report is defined this need to be initialized
  if (report) {
    reportStructure = getReportStructuresWithPath(reportStructure);
    // remove all empty node added by getMsdTree. this is required to avoid to add empty nodes in the next steps
    reportStructure = getFilteredTree(reportStructure, "metadataAttributes", repStrNode =>
      (!repStrNode.id || !repStrNode.id.match(/\d+\. \w+/g)));

    // iterates on report adding to all nodes "isFolder" taken from the corresponding node in reportStructures
    const reportRoot = [{
      isRoot: true,
      attributeSet: {
        reportedAttributes: (report && report.attributeSet && report.attributeSet.reportedAttributes)
          ? report.attributeSet.reportedAttributes
          : []
      }
    }];
    report = getMappedReportTree(reportRoot, repNode => {
      if (repNode.isRoot) {
        return repNode
      }
      const node = getNode(reportStructure, "metadataAttributes", node =>
        node.id === repNode.id.replace(/\d+. /g, ""));
      return {
        ...repNode,
        isFolder: node && node.isFolder,
        attributeSet: {
          reportedAttributes: (repNode && repNode.attributeSet && repNode.attributeSet.reportedAttributes)
            ? repNode.attributeSet.reportedAttributes
            : []
        }
      }
    });

    // iterates on report adding to all attribute the prefix "N. "
    report = getMappedReportTree(report, repNode => {
      let counts = {};
      (repNode.attributeSet.reportedAttributes || []).forEach(function (attr) {
        if (attr.isFolder) {
          counts[attr.id] = 1;
        }
      });
      repNode.attributeSet.reportedAttributes = (repNode.attributeSet.reportedAttributes || []).map(child => {
        if (!counts[child.id]) {
          return child;
        } else {
          const newAttr = {
            ...child,
            id: counts[child.id] + '. ' + child.id
          };
          counts[child.id] = counts[child.id] + 1;
          return newAttr
        }
      });
      return repNode;
    })[0].attributeSet.reportedAttributes;

    // iterates on report adding to all attribute the path from root
    report = report.map(attr => ({...attr, path: ''}));
    report = getMappedReportTree(report, repNode => ({
      ...repNode,
      attributeSet: {
        reportedAttributes: (repNode.attributeSet.reportedAttributes || []).map(child => ({
          ...child,
          path: repNode.path.length > 0
            ? repNode.path + '+' + repNode.id
            : repNode.id
        }))
      }
    }));

    // iterates on report to create or initialize the corresponding node in reportStructures
    getMappedReportTree(report, repNode => {
      const repStrNode = getNode(reportStructure, "metadataAttributes", repStrNode => {
        return (repStrNode.id === repNode.id.replace(/\d+. /g, "") && repStrNode.path === repNode.path);
      });

      if (repStrNode !== null) {
        const attachmentAnnot = getAttachmentAnnotationFromAnnotations(repNode, annotationsConfig);
        const attachment = getEmptyDictionaryForLanguages(dataLanguages, null);
        let attachmentName = getEmptyDictionaryForLanguages(dataLanguages, null);
        if (attachmentAnnot !== null && attachmentAnnot !== undefined) {
          attachmentName = {
            ...attachmentName,
            ...attachmentAnnot.texts
          }
        }
        let attachmentUploaded = getEmptyDictionaryForLanguages(dataLanguages, false);
        if (attachmentAnnot !== null && attachmentAnnot !== undefined) {
          Object.keys(attachmentAnnot.texts).forEach(key => {
            attachmentUploaded[key] = true;
          });
        }

        const customNoneIsAutoAnnotation = permissions && permissions.rules.includes("CanManageWorkingAnnotation")
          ? () => false
          : undefined;

        if (!repStrNode.isFolder) {
          repStrNode.annotations = _.uniqBy(
            repStrNode.annotations
              .concat(getFilteredAnnotations(repNode.annotations, annotationsConfig, (customNoneIsAutoAnnotation || isReferenceMetadataAutoAnnotation)))
              .map(annot => ({...annot, text: annot.texts, texts: undefined})),
            "type"
          );
          repStrNode.autoAnnotations = _.uniqBy(
            repStrNode.autoAnnotations
              .concat(getAutoAnnotations(repNode.annotations, annotationsConfig, (customNoneIsAutoAnnotation || isReferenceMetadataAutoAnnotation)))
              .map(annot => ({...annot, text: annot.texts, texts: undefined})),
            "type"
          );
          if (!repStrNode.isPresentational) {
            repStrNode.texts = getTexts(repStrNode, repNode);
            repStrNode.path = repNode.path;
            repStrNode.nodeKey = uuidv4();
            repStrNode.attachment = attachment;
            repStrNode.attachmentName = attachmentName;
            repStrNode.attachmentUploaded = attachmentUploaded;
            repStrNode.isPublic = getIsPublicFromAnnotations(repNode.annotations, annotationsConfig[AUTO_ANNOTATION_RESTRICTED_FOR_PUBLICATION_KEY].type);
          }

        } else {
          let emptyRepStrNode = repStrNode.metadataAttributes.find(child => child.id === (EMPTY_REPORT_ATTRIBUTE_KEY + repNode.id.replace(/\d+. /g, "")));
          if (emptyRepStrNode) {
            repStrNode.metadataAttributes.push({
              ...emptyRepStrNode,
              isFolder: null,
              id: `${repStrNode.metadataAttributes.length}. ${repStrNode.id}`,
              name: `${repStrNode.metadataAttributes.length}. ${repStrNode.name}`,
              nodeKey: uuidv4(),
              texts: getTexts(emptyRepStrNode, repNode),
              annotations: emptyRepStrNode.annotations
                .concat(getFilteredAnnotations(repNode.annotations, annotationsConfig, (customNoneIsAutoAnnotation || isReferenceMetadataAutoAnnotation)))
                .map(annot => ({...annot, text: annot.texts, texts: undefined})),
              autoAnnotations: getAutoAnnotations(repNode.annotations, annotationsConfig, (customNoneIsAutoAnnotation || isReferenceMetadataAutoAnnotation)),
              path: repNode.path,
              attachment: attachment,
              attachmentName: attachmentName,
              attachmentUploaded: attachmentUploaded,
              isPublic: getIsPublicFromAnnotations(repNode.annotations, annotationsConfig[AUTO_ANNOTATION_RESTRICTED_FOR_PUBLICATION_KEY].type)
            });
            repStrNode.metadataAttributes = getReportStructuresWithPath(repStrNode.metadataAttributes);
          }
        }
      } else {
        Modal.warning({
          title: t("components.reportStructureTree.modals.warning.attributeNotPresent.title"),
          content: t("components.reportStructureTree.modals.warning.attributeNotPresent.content", {attributeId: repNode.id})
        })
      }

      return repNode;
    });
  }

  return getFilteredTree(reportStructure, "metadataAttributes", node =>
    (!node.id || !node.id.includes(EMPTY_REPORT_ATTRIBUTE_KEY)));
};

// gets SDMXJson report from report structure
export const getSdmxJsonReportFromReportStructure = (reportStructure, report, annotationsConfig) => {

  // gets text from attribute
  const getTexts = attr => {
    if (attr.texts === null || attr.texts === undefined || attr.isPresentational || attr.isFolder) {
      return null
    }

    const attributeType = getAttributeType(attr).toLowerCase();

    let ret;

    if (attr.localRepresentation && attr.localRepresentation.enumeration) {
      ret = _.pickBy(attr.texts, val => (val || "").length > 0);

    } else if (attributeType === ATTRIBUTE_TEXT_TYPE_DATETIME) {
      ret = {
        und: moment(attr.texts).format(ATTRIBUTE_TEXT_TYPE_FORMAT_DATETIME)
      }

    } else if (attributeType === ATTRIBUTE_TEXT_TYPE_GREGORIANDAY) {
      ret = {
        und: moment(attr.texts).format(ATTRIBUTE_TEXT_TYPE_FORMAT_DATE)
      }

    } else if (attributeType === ATTRIBUTE_TEXT_TYPE_TIME) {
      ret = {
        und: moment(attr.texts).format(ATTRIBUTE_TEXT_TYPE_FORMAT_TIME)
      }

    } else if (attributeType === ATTRIBUTE_TEXT_TYPE_BOOLEAN) {
      ret = {
        und: attr.texts
      }

    } else if (attr.isMultiLang) {
      ret = _.pickBy(attr.texts, val => (val || "").length > 0);

    } else {
      ret = {
        und: attr.texts
      }
    }

    return _.forOwn(ret, (val, key) => ret[key] = encodeURIComponent(val));
  };

  // iterates on reportStructure to create or initialize the corresponding node in report
  reportStructure = getReportStructuresWithPath(reportStructure);
  getMappedTreeDeptFirst(reportStructure, "metadataAttributes", repStrNode => {
    if (!repStrNode.isFolder) {

      let annotations = (repStrNode.annotations || []);
      let autoAnnotations = (repStrNode.autoAnnotations || []);
      let allAnnotations = annotations.concat(autoAnnotations);

      // handling of restricted for publication annotation
      if (annotationsConfig) {
        const isNotPublicAnnotation = allAnnotations.find((ann) => (ann &&
          (ann.type || "").toLowerCase() === annotationsConfig[AUTO_ANNOTATION_RESTRICTED_FOR_PUBLICATION_KEY].type.toLowerCase()));
        if (repStrNode.isPublic) {
          if (isNotPublicAnnotation) {
            allAnnotations = allAnnotations.filter((ann) => (ann && 
              (ann.type || "").toLowerCase() !== annotationsConfig[AUTO_ANNOTATION_RESTRICTED_FOR_PUBLICATION_KEY].type.toLowerCase()));
          }
        } else {
          if (!isNotPublicAnnotation) {
            allAnnotations.push({
              id: annotationsConfig[AUTO_ANNOTATION_RESTRICTED_FOR_PUBLICATION_KEY].id,
              type: annotationsConfig[AUTO_ANNOTATION_RESTRICTED_FOR_PUBLICATION_KEY].type
            })
          }
        }
      }

      // handling of attachment annotation
      if (annotationsConfig) {
        allAnnotations = getAnnotationsWithAttachmentAnnot(repStrNode, allAnnotations, annotationsConfig);
      }

      const tempNode = {
        id: repStrNode.id,
        texts: getTexts(repStrNode),
        annotations: allAnnotations.length > 0
          ? allAnnotations.map(annot => ({...annot, text: undefined, texts: annot.text}))
          : null,
        attributeSet: {
          reportedAttributes: []
        },
        path: repStrNode.path
      };

      if (!repStrNode.parent) {
        report.attributeSet.reportedAttributes.push(tempNode);

      } else {
        const reportNode = getReportNode(report.attributeSet.reportedAttributes, attr =>
          repStrNode.path === `${attr.path.length > 0 ? (attr.path + '+') : ''}${attr.id}`
        );
        reportNode.attributeSet.reportedAttributes.push(tempNode);
      }
    }

    return repStrNode
  });

  // iterates on report to remove all unnecessary fields
  report.attributeSet.reportedAttributes = getMappedReportTree(report.attributeSet.reportedAttributes, repNode => ({
    id: repNode.id.replace(/\d+. /g, ""),
    texts: repNode.texts,
    annotations: repNode.annotations,
    attributeSet: (repNode && repNode.attributeSet && repNode.attributeSet.reportedAttributes && repNode.attributeSet.reportedAttributes.length > 0)
      ? repNode.attributeSet
      : undefined
  }));

  return report;
};

// iterates on reportStructures adding to all nodes the path to root node
const getReportStructuresWithPath = reportStructure => {

  let reportStructureWithPath = _.cloneDeep(reportStructure);

  reportStructureWithPath = reportStructureWithPath.map(node => ({...node, path: node.path ? node.path : ''}));
  reportStructureWithPath = getMappedTree(reportStructureWithPath, "metadataAttributes", node => ({
    ...node,
    metadataAttributes: node.metadataAttributes.map(child => {
      const childPath = !node.isFolder ? node.id : '';
      return ({
        ...child,
        path: (node.path.length > 0 && childPath.length > 0)
          ? node.path + '+' + childPath
          : node.path + childPath
      })
    })
  }));
  return reportStructureWithPath
};

/** annotations utilities **/

// check if an annotation is an autoAnnotation or a user one
const isReferenceMetadataAutoAnnotation = (annotation, annotationsConfig) => {
  const autoAnnotations = AUTO_ANNOTATIONS
    .concat(annotationsConfig[AUTO_ANNOTATION_FILE_PATH_KEY].type.toLowerCase())
    .concat(annotationsConfig[AUTO_ANNOTATION_RESTRICTED_FOR_PUBLICATION_KEY].type.toLowerCase())
    .concat(annotationsConfig[AUTO_ANNOTATION_IS_MULTILINGUAL_KEY].type.toLowerCase());

  return (
    (annotation.id && autoAnnotations.includes(annotation.id.toLowerCase())) ||
    (annotation.type && autoAnnotations.includes(annotation.type.toLowerCase()))
  )
};

// gets metadataflow triplet from annotations
export const getMetadataflowTripletFromAnnotations = metadataSet => {
  const annotations = (metadataSet && metadataSet.annotations) ? metadataSet.annotations : [];
  const autoAnnotations = (metadataSet && metadataSet.autoAnnotations) ? metadataSet.autoAnnotations : [];

  return ({
    id: ((annotations.concat(autoAnnotations).find(annot => annot.id === "MetadataflowId") || {}).text || {})["en"],
    agencyID: ((annotations.concat(autoAnnotations).find(annot => annot.id === "MetadataflowAgency") || {}).text || {})["en"],
    version: ((annotations.concat(autoAnnotations).find(annot => annot.id === "MetadataflowVersion") || {}).text || {})["en"]
  });
};

// gets db unique id annotation from annotations
export const getDbIdAnnotationFromAnnotations = (artefact, annotationId) => {
  const annotations = (artefact && artefact.annotations) ? artefact.annotations : [];
  const autoAnnotations = (artefact && artefact.autoAnnotations) ? artefact.autoAnnotations : [];

  return annotations.concat(autoAnnotations).find(annot => (annot.id || "").toLowerCase() === annotationId.toLowerCase());
};

// gets attachment annotation from annotations
export const getAttachmentAnnotationFromAnnotations = (attribute, annotationsConfig) => {
  const annotations = (attribute && attribute.annotations) ? attribute.annotations : [];
  const autoAnnotations = (attribute && attribute.autoAnnotations) ? attribute.autoAnnotations : [];

  return annotations.concat(autoAnnotations).find(annot => (annot.type || "").toLowerCase() === (annotationsConfig[AUTO_ANNOTATION_FILE_PATH_KEY].type || "").toLowerCase());
};

// check if an attribute is public or not from annotations
const getIsPublicFromAnnotations = (annotations, annotType) =>
  !(annotations || []).find(annot => (annot.type || "").toLowerCase() === annotType.toLowerCase());

const setReportStateAnnotations = (annotations, statusId, statusName) => {
  const stateIdAnnotation = (annotations || []).find(annot => (annot.id || "").toLowerCase() === REPORT_STATE_ID_ANNOTATION_ID.toLowerCase());
  const stateNameAnnotation = (annotations || []).find(annot => (annot.id || "").toLowerCase() === REPORT_STATE_NAME_ANNOTATION_ID.toLowerCase());

  if (stateIdAnnotation) {
    stateIdAnnotation.texts = {en: statusId};
    stateIdAnnotation.text = statusId;
  } else {
    annotations.push({
      id: REPORT_STATE_ID_ANNOTATION_ID,
      texts: {en: statusId},
      text: statusId
    })
  }

  if (stateNameAnnotation) {
    stateNameAnnotation.texts = {en: statusName};
    stateNameAnnotation.text = statusName;
  } else {
    annotations.push({
      id: REPORT_STATE_NAME_ANNOTATION_ID,
      texts: {en: statusName},
      text: statusName
    })
  }
};

export const updateReportStateAnnotations = (annotations, isDraft) => {

  if (isDraft) {
    setReportStateAnnotations(annotations, REPORT_STATE_DRAFT_ID, REPORT_STATE_DRAFT)
  } else {
    setReportStateAnnotations(annotations, REPORT_STATE_NOT_PUBLISHED_ID, REPORT_STATE_NOT_PUBLISHED)
  }

  return annotations
};

// checks if an attribute with children should be treated as presentational
export const getCustomIsPresentationalFromAnnotations = (msd, annotationsConfig) => {
  const annotations = (msd && msd.annotations) ? msd.annotations : [];
  const autoAnnotations = (msd && msd.autoAnnotations) ? msd.autoAnnotations : [];

  return !!(annotations.concat(autoAnnotations)).find((ann) => (ann && (ann.type || "").toLowerCase() === (annotationsConfig[AUTO_ANNOTATION_CUSTOM_IS_PRESENTATIONAL_KEY].type || "").toLowerCase()));
};

const getAnnotationsWithAttachmentAnnot = (attribute, annotations, annotationsConfig) => {
  let newAnnotations = _.cloneDeep(annotations || []);

  if (_(attribute.attachmentUploaded).pickBy(val => val === true).keys().value().length < 1) {
    newAnnotations = newAnnotations.filter(annot =>
      (annot.type || "").toLowerCase() !== (annotationsConfig[AUTO_ANNOTATION_FILE_PATH_KEY].type || "").toLowerCase()
    );

  } else {
    const fileAttachedAnnot = newAnnotations.find(annot => (annot.type || "").toLowerCase() === (annotationsConfig[AUTO_ANNOTATION_FILE_PATH_KEY].type || "").toLowerCase());
    const fileAttachedAnnotText = {}
    Object.keys(attribute.attachmentUploaded).forEach(lang => {
      if (attribute.attachmentUploaded[lang] === true) {
        fileAttachedAnnotText[lang] = attribute.attachmentName[lang];
      }
    });

    if (!fileAttachedAnnot) {
      newAnnotations.push({
        id: annotationsConfig[AUTO_ANNOTATION_FILE_PATH_KEY].id,
        type: annotationsConfig[AUTO_ANNOTATION_FILE_PATH_KEY].type,
        text: fileAttachedAnnotText
      });
    } else {
      fileAttachedAnnot.text = fileAttachedAnnotText;
    }
  }

  return newAnnotations;
};

/** utilities **/

// gets attribute type from attribute node
export const getAttributeType = attribute => (((attribute.localRepresentation || {}).textFormat || {}).textType || "");

const getClientBaseUrl = () => {
  const currUrl = window.location;
  const baseUrl = currUrl.protocol + "//" + currUrl.host + currUrl.pathname;
  return baseUrl + (baseUrl.slice(-1) === '/' ? '' : '/');
};
const templateBaseUrl = "static/referenceMetadata/template";

// gets the link to the metadata set html preview page
export const getMetadataSetHtmlPageUrl = (nodeId, metadataSetId, lang, metadataApiBaseUrl) => {
  const baseUrl = getClientBaseUrl();
  return `${baseUrl + templateBaseUrl}/MetadatasetTemplate.html` +
    `?metadataSetId=${metadataSetId}&lang=${lang}&BaseUrlMDA=${metadataApiBaseUrl}`
};

// gets the link to the DCAT-AP_IT metadata set html categories page
export const getDcatMetadataSetCategoryHtmlPageUrl = (nodeId, metadataSetId, lang, metadataApiBaseUrl) => {
  const baseUrl = getClientBaseUrl();
  return `${baseUrl + templateBaseUrl}/CategoryTemplate.html` +
    `?metadataSetId=${metadataSetId}&lang=${lang}&BaseUrlMDA=${metadataApiBaseUrl}`
};

// gets the link to the report html preview page
export const getReportHtmlPageUrl = (nodeId, metadataSetId, reportId, lang, metadataApiBaseUrl, hideHeader = false) => {
  const baseUrl = getClientBaseUrl();
  return `${baseUrl + templateBaseUrl}/GenericMetadataTemplate.html` +
    `?metadataSetId=${metadataSetId}&reportId=${reportId}&lang=${lang}&BaseUrlMDA=${metadataApiBaseUrl}${hideHeader ? "&showIntro=false" : ""}`
};

// gets the link to the MetadataApi "getMetadatada"
export const getMetadataApiReportUrl = (metadataApiBaseUrl, metadataSetId, reportId) => {
  return `${metadataApiBaseUrl}${metadataApiBaseUrl.slice(-1) === '/' ? '' : '/'}api/getMetadata?metadataSetId=${metadataSetId}&reportId=${reportId}`;
};

// gets the link to the MetadataApi "package_show"
export const getMetadataApiDcatReportUrl = (metadataApiBaseUrl, metadataSetId, reportId, lang) => {
  return `${metadataApiBaseUrl}${metadataApiBaseUrl.slice(-1) === '/' ? '' : '/'}${lang}/${metadataSetId}/api/3/action/package_show?id=${reportId}`;
};

// gets the link to the MetadataApi "package_search"
export const getMetadataApiDcatMetadataSetUrl = (metadataApiBaseUrl, metadataSetId, lang) => {
  return `${metadataApiBaseUrl}${metadataApiBaseUrl.slice(-1) === '/' ? '' : '/'}${lang}/${metadataSetId}/api/3/action/package_search`;
};

// gets the next available index for a folder node child
export const getNextAttributeIndexAvailable = array => {
  for (let i = 1; i <= array.length; i++) {
    if (!array.find(el => i === Number(el.id.substring(0, String(i).length)))) {
      return i
    }
  }
  return array.length + 1
};

// get targetReferenceValues
export const getTargetReferenceValuesStr = str => str.includes("urn:sdmx:")
  ? getStringFromArtefactTriplet(getArtefactTripletFromUrn(str))
  : str;